import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { logSafeErrorWithoutCustomerData } from '@atlassian/jira-common-directory-v2/src/utils/log-safe-error/index.tsx';
import { COMPONENTS_ERROR_ANALYTICS } from '../common/constant.tsx';
import { FETCH_COMPONENTS_REASON } from '../common/constants/fetch-components-reason.tsx';
import {
	type Action,
	getComponentsPageRequest,
	INITIATE_UPDATE_COMPONENT,
	initiateUpdateComponentFail,
	initiateUpdateComponentSuccess,
	UPDATE_COMPONENT_REQUEST,
	updateComponentFail,
	updateComponentSuccess,
	updateComponentValidationFail,
} from '../state/actions.tsx';
import { type ComponentRequestError, ComponentValidationError } from '../state/errors.tsx';
import { getCurrentPage } from '../state/query/selectors.tsx';
import type { State } from '../state/reducer.tsx';
import { requestComponent } from '../state/requests/retrieve.tsx';
import { updateComponent as updateComponentRequest } from '../state/requests/update.tsx';
import {
	getEditedComponentName,
	getId as getEditedComponentId,
} from '../state/ui/edit/selectors.tsx';

export const initiateUpdateComponent = (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<State>,
) => {
	const initiateUpdateComponent$ = action$.ofType(INITIATE_UPDATE_COMPONENT);

	return initiateUpdateComponent$.mergeMap(() => {
		const id = getEditedComponentId(store.getState());

		return Observable.fromPromise(requestComponent(store.getState(), id))
			.mergeMap((result) => Observable.of(initiateUpdateComponentSuccess(result)))
			.takeUntil(initiateUpdateComponent$)
			.catch((e) => {
				logSafeErrorWithoutCustomerData(
					e,
					'project-components.state.epics.initate.update.component',
					COMPONENTS_ERROR_ANALYTICS,
				);
				return Observable.of(
					initiateUpdateComponentFail({
						id,
						error: e,
					}),
				);
			});
	});
};

export const updateComponent = (action$: ActionsObservable<Action>, store: MiddlewareAPI<State>) =>
	action$.ofType(UPDATE_COMPONENT_REQUEST).mergeMap(() =>
		Observable.fromPromise(updateComponentRequest(store.getState()))
			.mergeMap(() =>
				Observable.of(
					updateComponentSuccess({
						componentName: getEditedComponentName(store.getState()),
					}),
					// @ts-expect-error - TS2769 - No overload matches this call.
					getComponentsPageRequest({
						page: getCurrentPage(store.getState()),
						initiatedBy: FETCH_COMPONENTS_REASON.COMPONENT_UPDATED,
					}),
				),
			)
			// @ts-expect-error - TS2345 - Argument of type '(error: ComponentRequestError) => Observable<UpdateComponentValidationFailAction> | Observable<UpdateComponentFailAction>' is not assignable to parameter of type '(err: any, caught: Observable<UpdateComponentSuccessAction>) => ObservableInput<UpdateComponentValidationFailAction>'.
			.catch((error: ComponentRequestError) => {
				logSafeErrorWithoutCustomerData(
					error,
					'project-components.state.epics.update',
					COMPONENTS_ERROR_ANALYTICS,
				);

				if (error instanceof ComponentValidationError) {
					return Observable.of(updateComponentValidationFail(error));
				}
				return Observable.of(updateComponentFail(error));
			}),
	);
